import React, { useState, useEffect } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Header from '../../../Components/adminDashboard/Header/Header';
import combinedServices from '../../../shared/services/user-service';
import Swal from 'sweetalert2';
import './ManageClasses.scss';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const ManageClasses = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => { fetchDuplicates(); }, []);

  const fetchDuplicates = async () => {
    setLoading(true);
    const res = await combinedServices.fetchDuplicateClasses();
    if (res?.status === 200) setGroups(res.data);
    setLoading(false);
    setPage(1);
  };

  const handleKeepThis = async (retainedId, allClasses) => {
    const deletedIds = allClasses.filter(c => c.id !== retainedId).map(c => c.id);
    const retained = allClasses.find(c => c.id === retainedId);

    const result = await Swal.fire({
      title: 'Keep this class?',
      html: `All references to the other <strong>${deletedIds.length}</strong> duplicate(s) will be replaced with <strong>${retained.name}</strong> (ID: ${retainedId}) and they will be deleted.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, keep & delete others',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#01458b',
    });
    if (!result.isConfirmed) return;

    Swal.fire({ title: 'Processing...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    const res = await combinedServices.deleteDuplicateClasses({ retainedId, deletedIds });
    if (res?.status === 200) {
      Swal.fire('Done!', 'Duplicates removed successfully.', 'success');
      fetchDuplicates();
    } else {
      Swal.fire('Error', res?.msg || 'Something went wrong', 'error');
    }
  };

  const totalPages = Math.ceil(groups.length / limit);
  const paginatedGroups = groups.slice((page - 1) * limit, page * limit);

  return (
    <>
      <Header />
      <div className="dbAdminRight">
        <div className="dbMid dbInner">
          <div className="flexBox spacebetween">
            <div className="dbMainHd">Manage Duplicate Classes</div>
            <button className="blueBtn" onClick={fetchDuplicates}>Refresh</button>
          </div>

          {loading ? (
            <div className="mc-loading">Loading duplicate classes...</div>
          ) : groups.length === 0 ? (
            <div className="whtBox mc-empty">
              <span className="material-icons-outlined">check_circle</span>
              <p>No duplicate classes found.</p>
            </div>
          ) : (
            <>
              <div className="mc-toolbar">
                <div className="mc-show-entries">
                  <label>Showing</label>
                  <select
                    className="formSelect"
                    value={limit}
                    onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                  >
                    {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <span>entries</span>
                </div>
                <p className="mc-summary">
                  {groups.length} duplicate group{groups.length !== 1 ? 's' : ''} found
                </p>
              </div>

              <div className="mc-table-wrap">
                {/* Fixed common table header */}
                <table className="mc-header-table">
                  <thead>
                    <tr>
                      <th className="mc-col-id">Class ID</th>
                      <th className="mc-col-name">Class Name</th>
                      <th className="mc-col-cat">Category ID</th>
                      <th className="mc-col-created">Created By</th>
                      <th className="mc-col-status">Status</th>
                      <th className="mc-col-action">Action</th>
                    </tr>
                  </thead>
                </table>

                {/* Accordion rows */}
                <Accordion>
                  {paginatedGroups.map((group, idx) => (
                    <Accordion.Item eventKey={String(idx)} key={idx} className="mc-accordion-item">
                      <Accordion.Header>
                        <div className="mc-accordion-header">
                          <span className="mc-class-name">{group.className}</span>
                          <span className="mc-badge-count">{group.duplicateClasses.length} duplicates</span>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body className="mc-accordion-body">
                        <table className="mc-body-table">
                          <tbody>
                            {group.duplicateClasses.map(cls => (
                              <tr key={cls.id}>
                                <td className="mc-col-id">{cls.id}</td>
                                <td className="mc-col-name">{cls.name}</td>
                                <td className="mc-col-cat">{cls.class_category_id}</td>
                                <td className="mc-col-created">{cls.created_by}</td>
                                <td className="mc-col-status">
                                  <span className={`mc-status ${cls.status === 'active' ? 'active' : 'inactive'}`}>
                                    {cls.status}
                                  </span>
                                </td>
                                <td className="mc-col-action">
                                  <button
                                    className="mc-keep-btn"
                                    onClick={() => handleKeepThis(cls.id, group.duplicateClasses)}
                                  >
                                    Keep This
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </div>

              {/* Pagination */}
              <div className="tblPagiWrp">
                <ul className="pagination namPagi">
                  <li>
                    <button onClick={() => setPage(p => p - 1)} disabled={page === 1}>Prev</button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <li key={i}>
                      <button
                        className={page === i + 1 ? 'active' : ''}
                        onClick={() => setPage(i + 1)}
                      >
                        {i + 1}
                      </button>
                    </li>
                  ))}
                  <li>
                    <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>Next</button>
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ManageClasses;